import React, { useRef } from 'react';

import { useOnVisible } from '../../../hooks/useOnVisible';
import { Badge } from '../../atoms/Badge/Badge';
import { FavouriteButton } from '../../atoms/FavouriteButton/FavouriteButton';
import { getDiscount } from './getDiscount';
import { ProductCardDelivery } from './ProductCardDelivery';
import { ProductCardMedia } from './ProductCardMedia';
import { ProductCardPrice } from './ProductCardPrice';
import { ProductCardRating } from './ProductCardRating';
import type { ProductCardMediaNavigatePayload, ProductCardProps } from './types';
import * as styles from './ProductCard.styles';

export const ProductCard = ({
  badge,
  delivery,
  favourite,
  isNew = false,
  isSponsored = false,
  onMediaNavigate,
  onProductClick,
  onProductMouseDown,
  onVisible,
  priority = false,
  product,
  promoteAsSale = false,
  promotion,
  rating,
}: ProductCardProps) => {
  const { availability, currencyCode, media, partner, price, title, url } = product;
  const cardRef = useRef<HTMLElement>(null);

  /* Passed conditionally rather than always, so a card with no handler builds no
     observer at all: a grid that does not track impressions pays nothing, and a
     consumer's test environment does not have to grow an IntersectionObserver. */
  useOnVisible(cardRef, onVisible && (() => onVisible({ product })));

  const discount = getDiscount(product);
  const isOutOfStock = availability === 'out_of_stock';

  const handleFavouriteClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    favourite?.onClick?.(e, { product });

  /* Bound to the anchors themselves rather than delegated from the card, so the
     favourite button — which sits outside the link — cannot reach it, and so the
     consumer's `currentTarget` is the link it clicked rather than the article. */
  const handleProductClick = (e: React.MouseEvent<HTMLAnchorElement>) =>
    onProductClick?.(e, { product });

  /* Bound to the same anchors as the click, since a middle click never produces a
     `click` and so is invisible to `onProductClick` alone. Unfiltered: which
     buttons matter is the consumer's rule, not the card's. */
  const handleProductMouseDown = (e: React.MouseEvent<HTMLAnchorElement>) =>
    onProductMouseDown?.(e, { product });

  /* Built only where there is something to report, rather than always, for the same
     reason `useOnVisible` above takes a handler conditionally: a card whose consumer
     does not track image interactions attaches no scroll listener of its own. */
  const handleMediaNavigate =
    onMediaNavigate &&
    ((payload: Omit<ProductCardMediaNavigatePayload, 'product'>) =>
      onMediaNavigate({ ...payload, product }));

  return (
    <article css={styles.card} data-testid="product-card" ref={cardRef}>
      <div css={styles.media}>
        <ProductCardMedia
          media={media}
          onLinkClick={handleProductClick}
          onLinkMouseDown={handleProductMouseDown}
          onNavigate={handleMediaNavigate}
          priority={priority}
          url={url}
        />
        {isOutOfStock && <span css={styles.outOfStockOverlay} data-testid="out-of-stock-overlay" />}
        {badge && (
          <span css={styles.badgeSlot}>
            {/* Compact, as the design has it: the badge sits over the product
                image and should not compete with it. */}
            <Badge icon={badge.icon} label={badge.label} size="compact" tone={badge.tone} />
          </span>
        )}
        {favourite && (
          <span css={styles.favouriteSlot}>
            <FavouriteButton
              active={favourite.active}
              label={favourite.label}
              onClick={handleFavouriteClick}
              size="sm"
            />
          </span>
        )}
      </div>

      <div css={styles.body}>
        {/* Everything from here to the divider flexes, so that in a grid the
            image above and the delivery block below stay level across cards
            however long each title runs. */}
        <div css={styles.details}>
          <p css={styles.partner}>{partner.name}</p>
          <h3 css={styles.title}>
            <a
              css={styles.titleLink}
              href={url}
              onClick={handleProductClick}
              onMouseDown={handleProductMouseDown}
            >
              {title}
            </a>
          </h3>

          <ProductCardPrice
            currencyCode={currencyCode}
            discount={discount}
            price={price}
            promoteAsSale={promoteAsSale}
            rating={<ProductCardRating isNew={isNew} rating={rating} />}
          />

          {promotion && (
            <p css={styles.promotion} data-testid="product-card-promotion">
              {'+ '}
              <span css={styles.promotionDiscount}>{promotion.label}</span>
              {' WITH CODE'}
            </p>
          )}
        </div>

        {(isOutOfStock || delivery) && (
          <>
            <hr css={styles.divider} />
            <div css={styles.deliverySlot}>
              {isOutOfStock ? (
                <p css={styles.outOfStock}>Out of stock</p>
              ) : (
                delivery && <ProductCardDelivery currencyCode={currencyCode} delivery={delivery} />
              )}
            </div>
          </>
        )}
      </div>

      {isSponsored && <p css={styles.sponsored}>Sponsored</p>}
    </article>
  );
};

ProductCard.displayName = 'ProductCard';
